import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../supabaseClient';

const GOLD = '#c8a04e';
const W  = (a) => `rgba(255,255,255,${a})`;
const GA = (a) => `rgba(200,160,78,${a})`;

const TIER_LABELS  = { '7_days': '7 Days', '30_days': '30 Days', 'lifetime': 'Lifetime' };
const TIER_AMOUNTS = { '7_days': '$5.00',  '30_days': '$10.00',  'lifetime': '$20.00'   };
const TIER_COLOR   = { '7_days': '#60a5fa', '30_days': '#4ade80', 'lifetime': GOLD };
const TIER_COLOR_A = { '7_days': '96,165,250', '30_days': '74,222,128', 'lifetime': '200,160,78' };

const STEPS = [
  {
    n: 1, title: 'Download Athena', desc: 'Grab the latest version from the downloads page.',
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={GA(0.75)} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
  },
  {
    n: 2, title: 'Login to the Athena Menu', desc: 'Open the app and sign in with your account.',
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={GA(0.75)} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>,
  },
  {
    n: 3, title: 'Activate Your Key When Prompted', desc: 'Paste your license key in the activation screen.',
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={GA(0.75)} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="7.5" cy="15.5" r="5.5"/><path d="M21 2l-9.6 9.6"/><path d="M15.5 7.5l3 3L22 7l-3-3"/></svg>,
  },
];

export default function OrderSuccess() {
  const [searchParams] = useSearchParams();
  const navigate       = useNavigate();
  const sessionId      = searchParams.get('session_id');

  const [loading, setLoading] = useState(true);
  const [order,   setOrder]   = useState(null);
  const [copied,  setCopied]  = useState(false);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    async function loadOrder() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user)      { navigate('/login');     return; }
      if (!sessionId) { navigate('/dashboard'); return; }

      let attempts = 0;
      const maxAttempts = 15;
      const poll = async () => {
        const { data, error: fetchError } = await supabase
          .from('v2_orders').select('*')
          .eq('user_id', user.id).eq('stripe_session_id', sessionId).maybeSingle();

        if (fetchError) { setError('Failed to load order.'); setLoading(false); return; }
        if (data)       { setOrder(data); setLoading(false); return; }
        if (attempts < maxAttempts) { attempts++; setTimeout(poll, 2000); return; }

        const { data: latest } = await supabase
          .from('v2_orders').select('*').eq('user_id', user.id).eq('status', 'completed')
          .order('created_at', { ascending: false }).limit(1).maybeSingle();

        if (latest) { setOrder(latest); } else { setError('Order not found. Please check your dashboard.'); }
        setLoading(false);
      };
      poll();
    }
    loadOrder();
  }, [sessionId, navigate]);

  const copyKey = async () => {
    if (!order?.license_key) return;
    await navigator.clipboard.writeText(order.license_key);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  const tc  = TIER_COLOR[order?.tier]   || GOLD;
  const tca = TIER_COLOR_A[order?.tier] || '200,160,78';

  const gridLines = [
    { left: '10%', delay: '0s' }, { left: '25%', delay: '2.2s' },
    { left: '42%', delay: '4.5s' }, { left: '58%', delay: '0.8s' },
    { left: '75%', delay: '3.1s' }, { left: '90%', delay: '1.6s' },
  ];

  /* ── Loading ── */
  if (loading) return (
    <div style={{ display: 'flex', height: '100vh', background: '#000', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter',sans-serif", position: 'relative', overflow: 'hidden' }}>
      <style>{`@keyframes osSpin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.04, backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='256' height='256'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='256' height='256' filter='url(%23n)'/%3E%3C/svg%3E")` }} />
      <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
        <div style={{ width: 36, height: 36, borderRadius: '50%', border: `2px solid ${GA(0.15)}`, borderTopColor: GOLD, animation: 'osSpin 0.8s linear infinite' }} />
        <p style={{ fontSize: 13, color: W(0.3), letterSpacing: '0.04em', margin: 0 }}>Confirming your order…</p>
      </div>
    </div>
  );

  /* ── Error ── */
  if (error) return (
    <div style={{ display: 'flex', height: '100vh', background: '#000', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter',sans-serif", padding: '24px' }}>
      <style>{`@keyframes osDrop2{0%{top:-20%}100%{top:110%}}`}</style>
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        {gridLines.map(({ left, delay }, i) => (
          <div key={i} style={{ position: 'absolute', width: 1, height: '100%', top: 0, left, background: W(0.055), overflow: 'hidden' }}>
            <div style={{ position: 'absolute', width: '100%', height: '15vh', left: 0, top: '-20%', background: `linear-gradient(to bottom,${GA(0)} 0%,${GA(0.65)} 75%,${GA(0.85)} 100%)`, animation: `osDrop2 7s ${delay} infinite cubic-bezier(0.4,0.26,0,0.97)` }} />
          </div>
        ))}
      </div>
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.04, backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='256' height='256'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='256' height='256' filter='url(%23n)'/%3E%3C/svg%3E")` }} />
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
        style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 420, background: 'radial-gradient(ellipse 120% 80% at 50% 0%,rgba(255,255,255,0.07) 0%,rgba(255,255,255,0.03) 55%,rgba(255,255,255,0.015) 100%)', border: `1px solid ${W(0.1)}`, borderRadius: 20, padding: '40px', boxShadow: `0 24px 60px rgba(0,0,0,0.7),inset 0 1px 0 ${W(0.06)}`, textAlign: 'center' }}>
        <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 180, damping: 16, delay: 0.1 }}
          style={{ width: 72, height: 72, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,80,80,0.08)', border: '1px solid rgba(255,80,80,0.18)', boxShadow: '0 0 28px rgba(255,80,80,0.08)', margin: '0 auto 20px' }}>
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
            <circle cx="14" cy="14" r="12" stroke="rgba(255,80,80,0.35)" strokeWidth="1.4" />
            <path d="M10 10l8 8M18 10l-8 8" stroke="rgba(255,80,80,0.8)" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </motion.div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#fff', margin: '0 0 8px', letterSpacing: '-0.02em' }}>Something went wrong</h2>
        <p style={{ fontSize: 13, color: W(0.32), lineHeight: 1.65, margin: '0 0 28px' }}>{error}</p>
        <div style={{ height: 1, background: `linear-gradient(90deg,transparent,${W(0.07)},transparent)`, marginBottom: 24 }} />
        <Link to="/dashboard"
          style={{ display: 'block', padding: '12px', borderRadius: 10, background: 'radial-gradient(ellipse 120% 80% at 50% 0%,rgba(200,160,78,0.12) 0%,rgba(200,160,78,0.04) 55%,rgba(255,255,255,0.02) 100%)', border: `1px solid ${GA(0.3)}`, boxShadow: `inset 0 1px 0 ${GA(0.1)}`, color: GOLD, fontWeight: 700, fontSize: 13.5, textDecoration: 'none', transition: 'all .2s' }}
          onMouseEnter={e => { e.currentTarget.style.background = 'radial-gradient(ellipse 120% 80% at 50% 0%,rgba(200,160,78,0.2) 0%,rgba(200,160,78,0.08) 55%,rgba(255,255,255,0.03) 100%)'; e.currentTarget.style.borderColor = GA(0.45); }}
          onMouseLeave={e => { e.currentTarget.style.background = 'radial-gradient(ellipse 120% 80% at 50% 0%,rgba(200,160,78,0.12) 0%,rgba(200,160,78,0.04) 55%,rgba(255,255,255,0.02) 100%)'; e.currentTarget.style.borderColor = GA(0.3); }}>
          Go to Dashboard
        </Link>
      </motion.div>
    </div>
  );

  /* ── Success: two-column Login-style layout ── */
  return (
    <div style={{ display: 'flex', height: '100vh', background: '#000', overflow: 'hidden', fontFamily: "'Inter',sans-serif" }}>
      <style>{`@keyframes osDrop3{0%{top:-20%}100%{top:110%}}`}</style>

      {/* ── LEFT: Activation guide (like Login steps) ── */}
      <div style={{ flex: 1, overflow: 'hidden', position: 'relative', background: '#000', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '0 64px' }}>

        {/* Grid */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
          {gridLines.map(({ left, delay }, i) => (
            <div key={i} style={{ position: 'absolute', width: 1, height: '100%', top: 0, left, background: W(0.055), overflow: 'hidden' }}>
              <div style={{ position: 'absolute', width: '100%', height: '15vh', left: 0, top: '-20%', background: `linear-gradient(to bottom,${GA(0)} 0%,${GA(0.65)} 75%,${GA(0.85)} 100%)`, animation: `osDrop3 7s ${delay} infinite cubic-bezier(0.4,0.26,0,0.97)` }} />
            </div>
          ))}
          {[0,1,2,3,4,5].map(i => <div key={i} style={{ position: 'absolute', left: 0, right: 0, top: `${Math.round((i/5)*100)}%`, height: 1, background: W(0.04) }} />)}
        </div>
        {/* Grain */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, opacity: 0.04, backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='256' height='256'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='256' height='256' filter='url(%23n)'/%3E%3C/svg%3E")` }} />
        {/* Right edge fade */}
        <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 80, background: 'linear-gradient(to right,transparent,#000)', zIndex: 2, pointerEvents: 'none' }} />

        <div style={{ position: 'relative', zIndex: 3 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: GOLD, letterSpacing: '0.12em', textTransform: 'uppercase', margin: '0 0 12px' }}>Get Started</p>
          <h2 style={{ fontSize: 28, fontWeight: 700, color: '#fff', margin: '0 0 48px', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
            Up and running<br/>in 3 steps.
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {STEPS.map(({ n, title, desc, icon }, idx) => (
              <div key={n}>
                <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: GA(0.08), border: `1px solid ${GA(0.18)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {icon}
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: GA(0.5), letterSpacing: '0.1em', marginBottom: 4 }}>STEP {n}</div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#fff', marginBottom: 4 }}>{title}</div>
                    <div style={{ fontSize: 13, color: W(0.38), lineHeight: 1.6 }}>{desc}</div>
                  </div>
                </div>
                {idx < STEPS.length - 1 && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 44, height: 48 }}>
                    <div style={{ width: 1, height: '100%', background: GA(0.25) }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── DIVIDER ── */}
      <div style={{ width: 1, background: 'rgba(255,255,255,0.07)', flexShrink: 0 }} />

      {/* ── RIGHT: Order details + license key (like Login form) ── */}
      <div style={{ width: '50%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 56px', overflow: 'hidden', position: 'relative' }}>
        {/* Grain */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0, opacity: 0.04, backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='256' height='256'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='256' height='256' filter='url(%23n)'/%3E%3C/svg%3E")` }} />

        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, ease: 'easeOut' }} style={{ width: '100%', maxWidth: 400, position: 'relative', zIndex: 1 }}>

          <div style={{ marginBottom: 28 }}>
            <h1 style={{ fontSize: 26, fontWeight: 700, color: '#fff', margin: '0 0 6px', letterSpacing: '-0.02em' }}>Order Confirmed</h1>
            <p style={{ fontSize: 13.5, color: W(0.35), margin: 0 }}>Payment successful — your key is ready.</p>
          </div>

          {/* Order rows */}
          <div style={{ background: 'radial-gradient(ellipse 120% 80% at 50% 0%,rgba(255,255,255,0.07) 0%,rgba(255,255,255,0.03) 55%,rgba(255,255,255,0.015) 100%)', border: `1px solid ${W(0.1)}`, borderRadius: 12, overflow: 'hidden', marginBottom: 16, boxShadow: `0 8px 24px rgba(0,0,0,0.3),inset 0 1px 0 ${W(0.06)}` }}>
            {[
              { label: 'Plan',     value: TIER_LABELS[order?.tier] || order?.tier, accent: true },
              { label: 'Amount',   value: TIER_AMOUNTS[order?.tier] || `$${((order?.amount||0)/100).toFixed(2)}` },
              { label: 'Date',     value: order?.created_at ? new Date(order.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—' },
              { label: 'Order ID', value: (order?.id||'').slice(0, 8).toUpperCase(), mono: true },
            ].map(({ label, value, accent, mono }, i, arr) => (
              <div key={label}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px' }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: W(0.28), textTransform: 'uppercase', letterSpacing: '0.1em' }}>{label}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: accent ? tc : '#fff', fontFamily: mono ? 'monospace' : 'inherit', opacity: mono ? 0.5 : 1 }}>{value}</span>
                </div>
                {i < arr.length - 1 && <div style={{ height: 1, background: W(0.05) }} />}
              </div>
            ))}
          </div>

          {/* License key */}
          <div style={{ marginBottom: 20 }}>
            <p style={{ fontSize: 10, fontWeight: 700, color: W(0.28), textTransform: 'uppercase', letterSpacing: '0.12em', margin: '0 0 8px' }}>Your License Key</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 16px', borderRadius: 12, background: 'radial-gradient(ellipse 120% 80% at 50% 0%,rgba(255,255,255,0.07) 0%,rgba(255,255,255,0.03) 55%,rgba(255,255,255,0.015) 100%)', border: `1px solid rgba(${tca},0.22)`, boxShadow: `0 8px 24px rgba(0,0,0,0.3),inset 0 1px 0 ${W(0.06)}` }}>
              <span style={{ flex: 1, fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: tc, letterSpacing: '0.06em', wordBreak: 'break-all' }}>{order?.license_key || '—'}</span>
              <button onClick={copyKey}
                style={{ flexShrink: 0, padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit', transition: 'all .15s', background: copied ? `rgba(${tca},0.12)` : W(0.04), color: copied ? tc : W(0.35), border: copied ? `1px solid rgba(${tca},0.28)` : `1px solid ${W(0.08)}` }}>
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Link to="/dashboard"
              style={{ display: 'block', padding: '13px', borderRadius: 10, background: 'radial-gradient(ellipse 120% 80% at 50% 0%,rgba(200,160,78,0.12) 0%,rgba(200,160,78,0.04) 55%,rgba(255,255,255,0.02) 100%)', border: `1px solid ${GA(0.3)}`, boxShadow: `inset 0 1px 0 ${GA(0.1)}`, color: GOLD, fontWeight: 700, fontSize: 13.5, textDecoration: 'none', textAlign: 'center', transition: 'all .2s' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'radial-gradient(ellipse 120% 80% at 50% 0%,rgba(200,160,78,0.2) 0%,rgba(200,160,78,0.08) 55%,rgba(255,255,255,0.03) 100%)'; e.currentTarget.style.borderColor = GA(0.45); }}
              onMouseLeave={e => { e.currentTarget.style.background = 'radial-gradient(ellipse 120% 80% at 50% 0%,rgba(200,160,78,0.12) 0%,rgba(200,160,78,0.04) 55%,rgba(255,255,255,0.02) 100%)'; e.currentTarget.style.borderColor = GA(0.3); }}>
              Go to Dashboard
            </Link>
            <Link to="/"
              style={{ display: 'block', padding: '13px', borderRadius: 10, background: 'radial-gradient(ellipse 120% 80% at 50% 0%,rgba(255,255,255,0.06) 0%,rgba(255,255,255,0.025) 55%,rgba(255,255,255,0.012) 100%)', border: `1px solid ${W(0.09)}`, boxShadow: `inset 0 1px 0 ${W(0.05)}`, color: W(0.45), fontWeight: 600, fontSize: 13.5, textDecoration: 'none', textAlign: 'center', transition: 'all .15s' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'radial-gradient(ellipse 120% 80% at 50% 0%,rgba(255,255,255,0.1) 0%,rgba(255,255,255,0.04) 55%,rgba(255,255,255,0.02) 100%)'; e.currentTarget.style.color = W(0.8); }}
              onMouseLeave={e => { e.currentTarget.style.background = 'radial-gradient(ellipse 120% 80% at 50% 0%,rgba(255,255,255,0.06) 0%,rgba(255,255,255,0.025) 55%,rgba(255,255,255,0.012) 100%)'; e.currentTarget.style.color = W(0.45); }}>
              Back to Homepage
            </Link>
          </div>
        </motion.div>

        <Link to="/" style={{ position: 'absolute', bottom: 28, fontSize: 12, color: W(0.2), textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6, transition: 'color .15s' }}
          onMouseEnter={e => e.currentTarget.style.color = W(0.5)} onMouseLeave={e => e.currentTarget.style.color = W(0.2)}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
          Back to Homepage
        </Link>
      </div>
    </div>
  );
}
